import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Heart, MessageCircle, Users, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';

const HeartlinesPage = () => {
  const depthLevels = [
    {
      name: 'KINDLE',
      emoji: '🕯️',
      description: 'Safe & comfortable',
      gradient: 'from-ember-spark to-ember-glow'
    },
    {
      name: 'SPARK', 
      emoji: '✨',
      description: 'Growing connection',
      gradient: 'from-ember-glow to-ember-flame'
    },
    {
      name: 'FLAME',
      emoji: '🔥', 
      description: 'Courageous sharing',
      gradient: 'from-ember-flame to-ember-fire'
    },
    {
      name: 'INFERNO',
      emoji: '💫',
      description: 'Sacred intimacy', 
      gradient: 'from-ember-fire to-ember-heat'
    }
  ];

  const researchFoundations = [
    {
      title: 'Psychological Safety',
      description: "Amy Edmondson's research on creating environments where people can share authentically"
    },
    {
      title: 'Emotional Intelligence',
      description: "Daniel Goleman's work on the social skills that enable deeper connection"
    },
    {
      title: 'Vulnerability Research',
      description: "Brené Brown's insights on courage and authentic sharing"
    },
    {
      title: 'Progressive Disclosure',
      description: 'Proven techniques from therapy and group dynamics'
    }
  ];

  const features = [
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Psychologically Safe',
      description: 'Our progressive depth system ensures no one feels forced beyond their comfort zone. Built on research-backed principles of psychological safety.',
      gradient: 'from-ember-spark to-ember-glow'
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: 'Expertly Curated', 
      description: 'Every question is carefully selected and tested. No more awkward silences or conversations that fizzle out before they begin.',
      gradient: 'from-ember-flame to-ember-fire'
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'Immediately Transformative',
      description: 'Complete facilitation guides and beautiful slide designs mean you can create meaningful moments right away—no training required.',
      gradient: 'from-ember-fire to-ember-heat'
    }
  ];

  const faqs = [
    {
      question: 'How long does a typical Heartlines™ session take?',
      answer: 'Most groups spend 60-90 minutes for a meaningful experience using 4-6 questions. You can also use individual questions for shorter check-ins (5-10 minutes each) or extend to 2+ hours for deeper exploration.'
    },
    {
      question: "What if someone isn't comfortable sharing deeply?",
      answer: "Participation is always voluntary in Heartlines™. Our progressive depth system allows people to engage at their comfort level, and many naturally progress as trust builds. We provide specific guidance for creating psychological safety."
    },
    {
      question: 'Can these be used in workplace teams?',
      answer: 'Absolutely! Many organizations use Heartlines™ for team building, leadership development, and creating psychological safety. We provide professional guidance and appropriate boundaries for workplace settings.'
    },
    {
      question: "What's included in the monthly subscription?",
      answer: 'Each month you receive 20 new questions (5 from each depth level), premium slide designs, facilitation guides, themed collections, and supporting templates. Content builds over 15 months into a comprehensive library.'
    }
  ];

  return (
    <>
      <SEOHead 
        title="Heartlines™ - Connection Questions That Guide From Surface to Sacred"
        description="Transform any gathering into meaningful connection through beautifully designed questions that guide conversations from surface-level sharing to transformational intimacy."
        keywords="heartlines, connection questions, meaningful conversations, team building, psychological safety, vulnerability, authentic sharing, leadership development"
        ogImage="https://images.pexels.com/photos/4670726/pexels-photo-4670726.jpeg"
        canonicalUrl="/heartlines"
      />
      
      <div className="min-h-screen bg-gradient-to-br from-neutral-warm via-white to-neutral-warm">
        <Navigation />
        
        {/* Hero Section */}
        <section className="pt-24 pb-16 relative overflow-hidden">
          {/* Background Elements */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-20 w-40 h-40 bg-ember-spark rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-20 right-20 w-32 h-32 bg-ember-heat rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
          </div>

          <div className="max-w-6xl mx-auto px-6 relative z-10">
            {/* Back Link */}
            <div className="mb-8">
              <Link to="/" className="inline-flex items-center text-ember-fire hover:text-ember-heat transition-colors font-medium">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Link>
            </div>

            <div className="text-center mb-16">
              <motion.h1 
                className="text-5xl md:text-7xl font-bold leading-tight mb-8"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <span className="block text-neutral-charcoal">From</span>
                <span className="block bg-gradient-to-r from-ember-fire to-ember-heat bg-clip-text text-transparent">small talk</span>
                <span className="block text-neutral-charcoal">to soul talk.</span>
              </motion.h1>
              
              <motion.div 
                className="max-w-4xl mx-auto mb-12"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <p className="text-xl text-neutral-medium leading-relaxed mb-6">
                  Heartlines™ transforms any gathering into meaningful connection through beautifully designed 
                  questions that guide conversations from surface-level sharing to transformational intimacy.
                </p>
                <p className="text-lg text-neutral-dark font-medium">
                  <em>Because every relationship deserves depth.</em>
                </p>
              </motion.div>

              {/* Depth Journey Visual */}
              <motion.div 
                className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mb-16"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                {depthLevels.map((level, index) => (
                  <motion.div 
                    key={level.name}
                    className="text-center group"
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <div className={`w-20 h-20 bg-gradient-to-br ${level.gradient} rounded-xl mx-auto mb-4 shadow-lg flex items-center justify-center text-2xl group-hover:shadow-xl transition-shadow`}>
                      {level.emoji}
                    </div>
                    <h3 className="font-bold text-neutral-charcoal mb-1">{level.name}</h3>
                    <p className="text-sm text-neutral-medium">{level.description}</p>
                  </motion.div>
                ))}
              </motion.div>

              <motion.div 
                className="flex flex-col sm:flex-row gap-4 justify-center items-center"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                <Link to="/contact?inquiry=custom">
                  <motion.button 
                    className="bg-gradient-to-r from-ember-fire to-ember-heat text-white px-10 py-4 rounded-xl font-semibold text-lg hover:shadow-xl transition-all"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Transform Your Conversations
                  </motion.button>
                </Link>
                <motion.button 
                  className="border-2 border-ember-fire text-ember-fire px-8 py-4 rounded-xl font-semibold text-lg hover:bg-ember-fire hover:text-white transition-all"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
                >
                  See How It Works
                </motion.button>
              </motion.div>
            </div>
          </div>
        </section>

        <div className="section-divider bg-gradient-to-r from-transparent via-ember-glow to-transparent h-0.5"></div>

        {/* Why This Matters */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-neutral-charcoal mb-6">
                Stop settling for 
                <span className="bg-gradient-to-r from-ember-fire to-ember-heat bg-clip-text text-transparent"> shallow connections</span>
              </h2>
              <p className="text-xl text-neutral-medium max-w-3xl mx-auto">
                In a world of endless small talk and surface-level interactions, Heartlines™ creates pathways 
                to the conversations that actually matter—the ones that change us.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-12">
              {features.map((feature, index) => (
                <motion.div 
                  key={feature.title}
                  className="text-center group"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                >
                  <motion.div 
                    className={`w-16 h-16 bg-gradient-to-br ${feature.gradient} rounded-xl mx-auto mb-6 shadow-lg flex items-center justify-center text-white`}
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    {feature.icon}
                  </motion.div>
                  <h3 className="text-xl font-bold text-neutral-charcoal mb-4">{feature.title}</h3>
                  <p className="text-neutral-medium leading-relaxed">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Research Foundation */}
        <section className="py-20 bg-gradient-to-br from-ember-depth to-ember-cool text-white">
          <div className="max-w-6xl mx-auto px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold mb-6">
                Built on decades of research
              </h2>
              <p className="text-xl opacity-90 mb-12 max-w-3xl mx-auto">
                Heartlines™ isn't just beautiful—it's backed by rigorous research in psychology, 
                organizational behavior, and human connection.
              </p>
            </motion.div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {researchFoundations.map((foundation, index) => (
                <motion.div 
                  key={foundation.title}
                  className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:bg-white/20 transition-all"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.05 }}
                >
                  <h4 className="text-lg font-semibold mb-3">{foundation.title}</h4>
                  <p className="text-sm opacity-90">{foundation.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section id="how-it-works" className="py-20 bg-gradient-to-br from-neutral-warm to-white">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-neutral-charcoal mb-6">
                Three steps to transformation
              </h2>
              <p className="text-xl text-neutral-medium">
                Simple to use, profound in impact
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-12">
              {[
                {
                  step: '1',
                  title: 'Choose Your Bundle',
                  description: 'Select a themed collection based on your group\'s needs—from building foundational trust to exploring life\'s deepest questions.',
                  gradient: 'from-ember-spark to-ember-glow'
                },
                {
                  step: '2', 
                  title: 'Follow the Guide',
                  description: 'Use our facilitation templates and safety guidelines to create the right environment for meaningful sharing.',
                  gradient: 'from-ember-flame to-ember-fire'
                },
                {
                  step: '3',
                  title: 'Experience Connection', 
                  description: 'Watch as your group naturally progresses from surface sharing to authentic, transformational conversations.',
                  gradient: 'from-ember-fire to-ember-heat'
                }
              ].map((step, index) => (
                <motion.div 
                  key={step.step}
                  className="text-center"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                >
                  <motion.div 
                    className={`w-16 h-16 bg-gradient-to-br ${step.gradient} rounded-xl mx-auto mb-6 flex items-center justify-center text-white font-bold text-xl shadow-lg`}
                    whileHover={{ scale: 1.1, rotate: 10 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    {step.step}
                  </motion.div>
                  <h3 className="text-xl font-bold text-neutral-charcoal mb-4">{step.title}</h3>
                  <p className="text-neutral-medium leading-relaxed">{step.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-neutral-charcoal mb-6">
                Choose your{' '}
                <span className="bg-gradient-to-r from-ember-fire to-ember-heat bg-clip-text text-transparent">connection journey</span>
              </h2>
              <p className="text-xl text-neutral-medium">
                Start with a foundation or commit to ongoing transformation
              </p>
            </motion.div>

            <div className="grid lg:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {/* Starter Pack */}
              <motion.div 
                className="bg-gradient-to-br from-neutral-warm to-white rounded-2xl p-8 shadow-xl border-2 border-ember-glow/30 hover:shadow-2xl transition-all"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02 }}
              >
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-ember-spark to-ember-glow rounded-xl mx-auto mb-4"></div>
                  <h3 className="text-2xl font-bold text-neutral-charcoal mb-2">Starter Pack</h3>
                  <p className="text-neutral-medium">Perfect for exploring the approach</p>
                </div>
                
                <div className="text-center mb-8">
                  <span className="text-4xl font-bold text-ember-fire">$39</span>
                  <span className="text-neutral-medium text-lg"> one-time</span>
                </div>

                <ul className="space-y-4 mb-8">
                  {[
                    '25 expertly curated questions',
                    'All 4 depth levels included',
                    'Beautiful slide deck design', 
                    'Quick-start facilitation guide',
                    'Planning templates',
                    '🎁 Bonus: 50% off first 3 months subscription'
                  ].map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-ember-spark mr-3 mt-0.5" />
                      <span className="text-neutral-dark">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link to="/contact?inquiry=custom">
                  <motion.button 
                    className="w-full bg-gradient-to-r from-ember-spark to-ember-glow text-white py-4 rounded-xl font-semibold text-lg hover:shadow-lg transition-all"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Try Heartlines™
                  </motion.button>
                </Link>
              </motion.div>

              {/* Monthly Subscription */}
              <motion.div 
                className="bg-gradient-to-br from-white to-neutral-warm rounded-2xl p-8 shadow-xl border-2 border-ember-fire hover:shadow-2xl transition-all relative"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02 }}
              >
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-ember-fire text-white px-6 py-2 rounded-full font-semibold text-sm">
                  Most Valuable
                </div>
                
                <div className="text-center mb-8 mt-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-ember-fire to-ember-heat rounded-xl mx-auto mb-4"></div>
                  <h3 className="text-2xl font-bold text-neutral-charcoal mb-2">Monthly Subscription</h3>
                  <p className="text-neutral-medium">15 months of evolving content</p>
                </div>
                
                <div className="text-center mb-8">
                  <span className="text-4xl font-bold text-ember-fire">$24</span>
                  <span className="text-neutral-medium text-lg">/month</span>
                </div>

                <ul className="space-y-4 mb-8">
                  {[
                    '20 new questions every month',
                    'Complete 4-level depth progression',
                    'Premium slide designs',
                    'Advanced facilitation guides',
                    'Monthly themed collections',
                    'Email templates & resources',
                    'Cancel anytime'
                  ].map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-ember-fire mr-3 mt-0.5" />
                      <span className="text-neutral-dark">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link to="/contact?inquiry=custom">
                  <motion.button 
                    className="w-full bg-gradient-to-r from-ember-fire to-ember-heat text-white py-4 rounded-xl font-semibold text-lg hover:shadow-lg transition-all"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Start Transformation
                  </motion.button>
                </Link>
              </motion.div>
            </div>

            {/* Launch Special */}
            <motion.div 
              className="max-w-2xl mx-auto mt-12 bg-gradient-to-br from-ember-spark/10 to-ember-heat/10 rounded-2xl p-8 border border-ember-fire/30 text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h4 className="text-2xl font-bold text-neutral-charcoal mb-4">🎁 Launch Special</h4>
              <p className="text-lg text-neutral-dark mb-6">
                Get the Starter Pack + your first month of subscription for just 
                <span className="text-3xl font-bold text-ember-fire"> $49</span>
              </p>
              <p className="text-neutral-medium mb-6">
                <em>Save $38 • Limited time for early adopters</em>
              </p>
              <Link to="/contact?inquiry=custom">
                <motion.button 
                  className="bg-gradient-to-r from-ember-fire to-ember-heat text-white px-10 py-4 rounded-xl font-semibold text-lg hover:shadow-xl transition-all"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Claim Launch Deal
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-gradient-to-br from-neutral-warm to-white">
          <div className="max-w-4xl mx-auto px-6">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-neutral-charcoal mb-6">
                Common questions
              </h2>
            </motion.div>

            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <motion.div 
                  key={index}
                  className="bg-white rounded-xl p-8 shadow-lg border border-neutral-light hover:shadow-xl transition-shadow"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <h4 className="text-lg font-bold text-neutral-charcoal mb-4">{faq.question}</h4>
                  <p className="text-neutral-medium leading-relaxed">{faq.answer}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-24 bg-gradient-to-br from-ember-depth to-ember-cool text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-5xl font-bold mb-8">
                Your next conversation could 
                <span className="text-ember-spark"> change everything.</span>
              </h2>
              <p className="text-xl opacity-90 mb-12 max-w-3xl mx-auto leading-relaxed">
                Because in a world of surface connections, the courage to go deeper 
                isn't just personal transformation—it's revolutionary.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                <Link to="/contact?inquiry=custom">
                  <motion.button 
                    className="bg-ember-gradient text-white px-12 py-4 rounded-xl font-semibold text-lg hover:shadow-xl transition-all ember-glow"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Transform Your Conversations
                  </motion.button>
                </Link>
                <Link to="/">
                  <motion.button 
                    className="text-ember-spark hover:text-white transition-colors text-lg font-semibold border-2 border-ember-spark hover:border-white px-8 py-4 rounded-xl"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Explore Other EMBER Tools →
                  </motion.button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default HeartlinesPage;